import type { JobWithRelations } from "@/types/db";
import type { BreadcrumbItem } from "@/components/common/Breadcrumb";
import Breadcrumb from "@/components/common/Breadcrumb";
import JobApplyButton from "./jobApplyBtn";
import JobTagsGrouped from "./jobTagsGrouped";
import { formatLocation, formatSalaryPeriod } from "@/lib/utils";
import { companyDisplayName } from "@/lib/jobLifecycle";

interface JobDetailContentProps {
  job: JobWithRelations;
  breadcrumbs?: BreadcrumbItem[];
}

export default function JobDetailContent({
  job,
  breadcrumbs,
}: JobDetailContentProps) {
  const isExpired = job.isActive === false;
  const companyName = companyDisplayName(job);
  const workOption = job.jobTags?.find(
    (tag) => tag.category?.name === "Work Options",
  );
  const jobType = job.jobTags?.find((tag) => tag.category?.name === "Job Type");

  const formattedDate = job.createdAt
    ? new Date(job.createdAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null;

  const location = formatLocation(job);
  const salary =
    job.minSalary != null &&
    job.maxSalary != null &&
    job.salaryCurrency &&
    job.salaryPeriod
      ? `${job.salaryCurrency} ${job.minSalary.toLocaleString()}–${job.maxSalary.toLocaleString()} / ${formatSalaryPeriod(job.salaryPeriod)}`
      : null;

  const metaItems = [
    jobType?.name ?? "Full Time",
    workOption?.name ?? (job.isRemote ? "Remote" : "In Office"),
    location || null,
  ].filter(Boolean);

  const metaItems2 = [
    salary,
    formattedDate ? `Posted ${formattedDate}` : null,
  ].filter(Boolean);

  return (
    <div
      id={`jobDetail-${job.id}`}
      className="jobDetail flex min-h-[85vh] w-full flex-col pb-24 sm:pb-0"
    >
      {/* Header — full-width bg, centered content */}
      <header className="border-b border-orange-100 bg-orange-50">
        <div className="mx-auto max-w-4xl px-4 pt-16 pb-14 text-start sm:px-8 md:px-12">
          {/* Breadcrumb */}
          {breadcrumbs && breadcrumbs.length > 0 && (
            <div className="mb-6">
              <Breadcrumb items={breadcrumbs} />
            </div>
          )}

          {/* Expired banner */}
          {isExpired && (
            <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              This job listing has expired. Browse similar roles below.
            </div>
          )}

          {/* Company */}
          <p className="text-base font-semibold text-gray-500">{companyName}</p>

          {/* Title */}
          <h1 className="mt-2 mb-4 text-3xl font-semibold tracking-tight text-balance text-gray-900 sm:text-4xl lg:text-5xl">
            {job.role}
          </h1>

          {/* Metadata row 1 */}
          {metaItems.length > 0 && (
            <p className="mt-3 text-base text-gray-600">
              {metaItems.join("  ·  ")}
            </p>
          )}

          {/* Metadata row 2 */}
          {metaItems2.length > 0 && (
            <p className="mt-1 text-sm text-gray-500">
              {metaItems2.join("  ·  ")}
            </p>
          )}

          {/* Tags grouped by category */}
          <JobTagsGrouped tags={job.jobTags} />

          {/* Apply Button */}
          {!isExpired && (
            <div className="mt-6 hidden sm:inline-flex">
              <JobApplyButton job={job} variant="detail_desktop" />
            </div>
          )}
        </div>
      </header>

      {/* Job Content — full-width bg, centered content */}
      <div className="mx-auto max-w-4xl px-4 py-10 text-left text-sm sm:px-8 sm:text-base md:text-lg">
        <div
          className="prose max-w-none"
          dangerouslySetInnerHTML={{ __html: job.content || "" }}
        />
      </div>

      {/* Bottom Apply Button — desktop only; mobile uses the sticky bar */}
      {!isExpired && (
        <div className="mx-auto hidden w-3/4 max-w-4xl flex-col items-center justify-center sm:flex">
          <JobApplyButton job={job} variant="detail_bottom" />
        </div>
      )}

      {/* Helpful nudge — visible on every viewport */}
      {!isExpired && (
        <p className="mx-auto mt-3 max-w-md px-4 text-center text-sm text-gray-500">
          Mention you found this on Data First Jobs — it helps us bring you more
          roles like this.
        </p>
      )}

      {/* Sticky Footer Banner — mobile only */}
      {!isExpired && (
        <div className="fixed right-0 bottom-0 left-0 z-40 border-t border-gray-200 bg-white shadow-[0_-2px_10px_rgba(0,0,0,0.06)] sm:hidden">
          <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3 sm:px-6">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-gray-900 sm:text-base">
                {job.role}
              </p>
              <p className="truncate text-xs text-gray-500 sm:text-sm">
                {companyName}
              </p>
            </div>
            <div className="ml-4 shrink-0">
              <JobApplyButton job={job} variant="mobile_sticky" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
